import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, Pencil, Trash2, Bug, Search } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import ProductDebug from './ProductDebug';

interface ShopifyProduct {
    id: string;
    title: string;
    variants: {
        id: string;
        title: string;
        price: string;
        sku: string;
    }[];
    isConfigured?: boolean; // Flag to indicate if this product is already configured
}

interface ProductConfig {
    id: number;
    product_id: string;
    product_name: string;
    days_supply: number;
    processing_days: number;
    is_cannabis: boolean;
    is_available: boolean;
}

export default function ProductConfig() {
    const [configs, setConfigs] = useState<ProductConfig[]>([]);
    // We keep this for API calls but don't need to reference it directly in JSX
    const [_, setProducts] = useState<ShopifyProduct[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showDebug, setShowDebug] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [inlineEditData, setInlineEditData] = useState<{[key: number]: {
        days_supply: number;
        processing_days: number;
        is_cannabis: boolean;
    }}>({});
    
    // Add search state
    const [searchTerm, setSearchTerm] = useState("");

    // No longer need to track available products since we don't have an add form
    
    useEffect(() => {
        fetchProductConfigs();
        fetchShopifyProducts();
    }, []);
    
    // No longer need to update available products

    const fetchProductConfigs = async () => {
        try {
            // We'll use query param to control whether to show unavailable products
            const showUnavailable = false; // Default to only showing available products
            
            const response = await fetch(`/api/admin/product-configs?showUnavailable=${showUnavailable}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setConfigs(data.products || []); // Handle the new response structure
                console.log(`Loaded ${data.products?.length || 0} products from API (${data.availableCount} available)`);
                // No longer need to update available products
            } else {
                toast({
                    variant: "destructive",
                    title: "Error",
                    description: "Failed to fetch product configurations"
                });
            }
        } catch (error) {
            console.error('Error loading configurations:', error);
            toast({
                variant: "destructive",
                title: "Error",
                description: "Error loading configurations"
            });
        }
    };

    const fetchShopifyProducts = async (refresh = false) => {
        try {
            setIsLoading(true);
            const url = refresh 
                ? '/api/admin/shopify-products?refresh=true&nocache=' + new Date().getTime()
                : '/api/admin/shopify-products?nocache=' + new Date().getTime();
                
            console.log(`Fetching products from ${url}`);
                
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Cache-Control': 'no-cache'
                }
            });

            if (response.ok) {
                const data = await response.json();
                console.log(`Received ${data.length} products from API`);
                
                // Check for specific products
                const zincProduct = data.find((p: any) => p.title.includes('Zinc'));
                const vitaminBProduct = data.find((p: any) => p.title.includes('VITAMIN B COMPLEX'));
                
                console.log('Debug - Zinc product:', zincProduct ? zincProduct.title : 'Not found');
                console.log('Debug - Vitamin B product:', vitaminBProduct ? vitaminBProduct.title : 'Not found');
                
                setProducts(data);
                // No longer need to update available products
                
                if (refresh) {
                    toast({
                        title: "Success",
                        description: `Products refreshed from Shopify (${data.length} products)`
                    });
                }
            } else {
                const errorData = await response.json();
                console.error('API Error:', errorData);
                toast({
                    variant: "destructive",
                    title: "Error",
                    description: errorData.message || "Failed to fetch Shopify products"
                });
            }
        } catch (error) {
            console.error('Error loading Shopify products:', error);
            toast({
                variant: "destructive",
                title: "Error",
                description: "Error loading products"
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Start inline editing for a specific config
    const startInlineEdit = (config: ProductConfig) => {
        // Initialize the inline edit data for this config
        setInlineEditData({
            ...inlineEditData,
            [config.id]: {
                days_supply: config.days_supply,
                processing_days: config.processing_days,
                is_cannabis: config.is_cannabis || false
            }
        });
        setEditingId(config.id);
    };
    
    // Cancel inline editing
    const cancelInlineEdit = () => {
        setEditingId(null);
    };
    
    // Save inline edit changes
    const saveInlineEdit = async (config: ProductConfig) => {
        try {
            const editData = inlineEditData[config.id];
            if (!editData) return;
            
            const response = await fetch(`/api/admin/product-configs/${config.id}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    product_id: config.product_id,
                    product_name: config.product_name,
                    days_supply: editData.days_supply,
                    processing_days: editData.processing_days,
                    is_cannabis: editData.is_cannabis
                })
            });

            if (response.ok) {
                toast({
                    title: "Success",
                    description: "Configuration updated"
                });
                fetchProductConfigs();
                setEditingId(null);
            } else {
                const error = await response.json();
                toast({
                    variant: "destructive",
                    title: "Error",
                    description: error.message || "Failed to update configuration"
                });
            }
        } catch (error) {
            console.error('Error updating configuration:', error);
            toast({
                variant: "destructive",
                title: "Error",
                description: "Error updating configuration"
            });
        }
    };
    
    // Update inline edit data
    const updateInlineEditField = (configId: number, field: string, value: any) => {
        setInlineEditData({
            ...inlineEditData,
            [configId]: {
                ...inlineEditData[configId],
                [field]: value
            }
        });
    };
    
    // We no longer need the add product function since products are automatically synced from Shopify

    const handleDelete = async (id: number) => {
        if (!confirm('Are you sure you want to delete this configuration?')) {
            return;
        }

        try {
            const response = await fetch(`/api/admin/product-configs/${id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (response.ok) {
                toast({
                    title: "Success",
                    description: "Configuration deleted successfully"
                });
                fetchProductConfigs();
            } else {
                toast({
                    variant: "destructive",
                    title: "Error",
                    description: "Failed to delete configuration"
                });
            }
        } catch (error) {
            console.error('Error deleting configuration:', error);
            toast({
                variant: "destructive",
                title: "Error",
                description: "Error deleting configuration"
            });
        }
    };

    // We no longer need the form submission function since we're using inline editing
    
    // No longer need the updateAvailableProducts function

    // Filter products based on search term
    const filteredConfigs = searchTerm.trim() === '' 
        ? configs 
        : configs.filter(config => 
            config.product_name.toLowerCase().includes(searchTerm.toLowerCase())
        );

    return (
        <div className="p-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold">Product Configurations</h1>
                <div className="flex space-x-2">
                    <Button 
                        variant="outline" 
                        onClick={() => fetchShopifyProducts(true)}
                        disabled={isLoading}
                    >
                        {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                        Refresh Shopify Products
                    </Button>
                    <Button 
                        variant="outline" 
                        onClick={() => setShowDebug(!showDebug)}
                    >
                        <Bug className="mr-2 h-4 w-4" /> {showDebug ? 'Hide Debug' : 'Debug'}
                    </Button>
                </div>
            </div>

            {showDebug && (
                <div className="mb-6">
                    <ProductDebug />
                </div>
            )}
            
            {/* Search box */}
            <div className="mb-4">
                <div className="relative w-full max-w-md">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <Search className="w-4 h-4 text-gray-500" />
                    </div>
                    <Input
                        type="text"
                        placeholder="Search products..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10" // Add padding for the icon
                    />
                    {searchTerm && (
                        <button 
                            className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                            onClick={() => setSearchTerm("")}
                            title="Clear search"
                        >
                            &times;
                        </button>
                    )}
                </div>
                <div className="text-sm text-gray-500 mt-1">
                    {searchTerm ? `Found ${filteredConfigs.length} matching products` : `Showing all ${configs.length} products`}
                </div>
            </div>

            {isLoading ? (
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="h-8 w-8 animate-spin" />
                </div>
            ) : configs.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                    No product configurations found
                </div>
            ) : filteredConfigs.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                    No products match your search "{searchTerm}" - try a different search term
                </div>
            ) : (
                <div className="grid gap-4">
                    {filteredConfigs.map((config) => (
                        <Card key={config.id}>
                            <CardContent className="p-6">
                                <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                                    {/* Product Name - always read-only */}
                                    <div>
                                        <label className="text-sm font-medium">Product Name</label>
                                        <div className="mt-1">{config.product_name}</div>
                                    </div>
                                    
                                    {/* Product ID - always read-only */}
                                    <div>
                                        <label className="text-sm font-medium">Product ID</label>
                                        <div className="mt-1">{config.product_id}</div>
                                    </div>
                                    
                                    {/* Days Supply - editable when in edit mode */}
                                    <div>
                                        <label className="text-sm font-medium">Days Supply</label>
                                        {editingId === config.id ? (
                                            <Input 
                                                type="number"
                                                className="mt-1"
                                                value={inlineEditData[config.id]?.days_supply || config.days_supply}
                                                onChange={(e) => updateInlineEditField(
                                                    config.id, 
                                                    'days_supply', 
                                                    parseInt(e.target.value) || 0
                                                )}
                                                min="1"
                                            />
                                        ) : (
                                            <div className="mt-1">{config.days_supply}</div>
                                        )}
                                    </div>
                                    
                                    {/* Processing Days - editable when in edit mode */}
                                    <div>
                                        <label className="text-sm font-medium">Processing Days</label>
                                        {editingId === config.id ? (
                                            <Input 
                                                type="number"
                                                className="mt-1"
                                                value={inlineEditData[config.id]?.processing_days || config.processing_days}
                                                onChange={(e) => updateInlineEditField(
                                                    config.id, 
                                                    'processing_days', 
                                                    parseInt(e.target.value) || 0
                                                )}
                                                min="1"
                                            />
                                        ) : (
                                            <div className="mt-1">{config.processing_days}</div>
                                        )}
                                    </div>
                                    
                                    {/* Product Type - editable when in edit mode */}
                                    <div>
                                        <label className="text-sm font-medium">Product Type</label>
                                        {editingId === config.id ? (
                                            <div className="flex items-center mt-2">
                                                <input
                                                    type="checkbox"
                                                    checked={inlineEditData[config.id]?.is_cannabis || false}
                                                    onChange={(e) => updateInlineEditField(
                                                        config.id,
                                                        'is_cannabis',
                                                        e.target.checked
                                                    )}
                                                    className="h-4 w-4 mr-2"
                                                />
                                                <span className="text-sm">Cannabis Product</span>
                                            </div>
                                        ) : (
                                            <div className="mt-1">
                                                {config.is_cannabis ? 
                                                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                                        Cannabis (6 months)
                                                    </span> : 
                                                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                                        Standard (12 months)
                                                    </span>
                                                }
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Availability Status - always read-only */}
                                    <div>
                                        <label className="text-sm font-medium">Status</label>
                                        <div className="mt-1">
                                            {config.is_available ? 
                                                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                                    Available
                                                </span> : 
                                                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                                                    Not Available
                                                </span>
                                            }
                                        </div>
                                    </div>
                                    
                                    {/* Action Buttons */}
                                    <div className="flex items-center justify-end space-x-2">
                                        {editingId === config.id ? (
                                            <>
                                                <Button
                                                    variant="default"
                                                    size="sm"
                                                    onClick={() => saveInlineEdit(config)}
                                                >
                                                    Save
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={cancelInlineEdit}
                                                >
                                                    Cancel
                                                </Button>
                                            </>
                                        ) : (
                                            <>
                                                <Button
                                                    variant="outline"
                                                    onClick={() => startInlineEdit(config)}
                                                >
                                                    <Pencil className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    onClick={() => handleDelete(config.id)}
                                                    className="text-red-500 hover:text-red-700"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}